import { Component, OnInit, ChangeDetectionStrategy, Input, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GetTicketsResponse, Ticket } from '@kempoweroyj/ngx-ticketing-api-client';
import { distinctUntilChanged } from 'rxjs/operators';
import { LocationsService } from 'shared/stations-service/locations.service';
import { TicketsService } from 'shared/tickets-service/tickets.service';
import { BetaLinkService } from 'layout/layout-service/beta-link.service';

export interface TicketsByStationIds {
    [stationId: string]: GetTicketsResponse;
}
@Component({
    selector: 'ce-tile-ticket',
    templateUrl: './tile-ticket.component.html',
    styleUrls: ['./tile-ticket.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class TileTicketComponent implements OnInit {
    @Input() stationId: string;
    @Input() locationId: string;

    _stationIds: string[];

    ticketsByStationIds: TicketsByStationIds = {};
    tickets: Ticket[] = [];

    ticketsLoaded = false;

    loading = true;

    // i18n texts
    notesText = $localize`:Tickets text|:Notes`;

    /** Tenants with support portal feature access get the CE beta Support portal instead of kempower.com */
    showBetaSupportPortal$ = this.betaLinkService.showBetaSupportPortal$;
    supportPortalLink = this.betaLinkService.supportPortalLink;

    constructor(
        private cdRef: ChangeDetectorRef,
        private ticketsService: TicketsService,
        private locationsService: LocationsService,
        private router: Router,
        private betaLinkService: BetaLinkService
    ) {}

    ngOnInit(): void {
        if (this.locationId) {
            this.locationsService
                .locationStations$(this.locationId)
                .pipe(distinctUntilChanged(), untilDestroyed(this))
                .subscribe((ss) => {
                    // delete old station ids
                    this._stationIds = [];
                    this._stationIds = ss.map((s) => s.stationId);

                    for (const _id of this._stationIds) {
                        this.ticketsService
                            .tickets$(_id)
                            .pipe(untilDestroyed(this))
                            .subscribe((tickets) => {
                                this.ticketsByStationIds[_id] = { tickets: [] };

                                this.ticketsByStationIds[_id] = tickets;

                                this.tickets = mergeTicketsInOneLocation(this.ticketsByStationIds)?.tickets;
                                this.updateTickets();
                            });
                    }
                });
        } else if (this.stationId) {
            this.ticketsService
                .tickets$(this.stationId)
                .pipe(untilDestroyed(this))
                .subscribe((tickets) => {
                    this.tickets = tickets?.tickets;
                    this.updateTickets();
                });
        }
    }

    updateTickets() {
        this.ticketsLoaded = true;
        this.loading = false;
        this.cdRef.markForCheck();
    }

    openTicketList() {
        // !FIXME: locationId will navigate to show all tickets and not tickets of that location
        if (this.stationId) {
            this.router.navigate(['', { outlets: { d: ['notes', this.stationId] } }]);
        } else {
            this.router.navigate(['', { outlets: { d: ['notes'] } }]);
        }
    }

    createNewTicket() {
        this.router.navigate(['', { outlets: { d: ['notes', this.stationId, 'new'] } }]);
    }
}

function mergeTicketsInOneLocation(blob: TicketsByStationIds) {
    const _all = { tickets: [] };

    Object.values(blob).forEach((station) => {
        if (station?.tickets && station.tickets?.length) {
            _all.tickets.push(...(station?.tickets || []));
        }
    });

    // Return
    return _all;
}
